import React, { useState } from 'react';
import { actions, containers, feedback } from '@ecosystem/newron-design-system';
import classNames from 'classnames';
import { DeleteSavedCustomModal } from './deleteSavedCustomModal';
import { CustomSegmentType } from '../types';
import { getCustomSegmentsLabelDetails, isSavedCustomPriceClass, SavedCustomSegmentsType } from './customSegmentsUtils';
import { isViewer } from '../utils';
import { authentication } from '@ecosystem/newron-client-lib';
import { FilterType } from '../../types';
import { CustomCategoricalClass, CustomNumericalClass } from '../../types/content/custom.types';

const { Button } = actions;
const { Badge } = feedback;
const { Accordion } = containers;

type Props = {
  /** Custom Segment Type (customPriceClass, customFeatureSegmentation...) */
  customSegmentType: CustomSegmentType;
  /** Save Card Header Label */
  savedCardLabels: string[];
  /** saved custom segmentation */
  savedData: SavedCustomSegmentsType;
  /** is current card active or not */
  isActive: boolean;
  /** is current card applicable or not */
  isApplicable: boolean;
  /** callback function for applying */
  handleApply: (savedData: SavedCustomSegmentsType) => void;
  /** callback function for deleting */
  handleDelete: (savedData: SavedCustomSegmentsType, userId?: string) => void;
  /**callback function for sharing */
  handleShare?: (savedData: SavedCustomSegmentsType) => void;
};

/**
 * The card component used to display the Saved Custom Segmentation for Price Class and Feature Segmentation
 */
export const SavedCustomSegmentsCard = ({
  customSegmentType,
  savedCardLabels,
  savedData,
  isActive,
  isApplicable,
  handleApply,
  handleDelete,
  handleShare,
}: Props) => {
  const owner = authentication.getUser();

  const customSegmentDetails = getCustomSegmentsLabelDetails(savedData)[customSegmentType];

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  //this function is to open/close the modal
  const toggleDeleteModal = () => {
    setShowDeleteModal(prev => !prev);
  };

  /**
   * Renders the Delete Modal when the delete button is clicked
   * @returns - the Delete Modal
   */
  const renderDeleteSavedFeatureSegmentsModal = () => {
    return (
      <DeleteSavedCustomModal
        header={customSegmentDetails.deleteSavedModalLabelText.header}
        onClose={toggleDeleteModal}
        infoText={customSegmentDetails.deleteSavedModalLabelText.infoText}
        subText={customSegmentDetails.deleteSavedModalLabelText.subText}
        customClassModalType={customSegmentType}
        isOpen={showDeleteModal}
        onDelete={() => {
          handleDelete(savedData, owner.userId);
        }}
      />
    );
  };

  const renderAccordionDetails = (savedData: SavedCustomSegmentsType) => {
    const savedCustomSegmentDetails = isSavedCustomPriceClass(savedData)
      ? savedData.priceClasses
      : savedData.customSegmentation;

    const getLabel = (savedCustomSegmentDetail: CustomNumericalClass | CustomCategoricalClass, index: number) => {
      let label = savedCustomSegmentDetail.label;

      if (isSavedCustomPriceClass(savedData) || savedData.filterType === FilterType.numericRange) {
        const customNumericClass = savedCustomSegmentDetail as CustomNumericalClass;
        const min = customNumericClass.min != null ? ` (${customNumericClass.min} ` : ' (';
        const lastIndex = savedCustomSegmentDetails.length - 1;
        label += min;
        if (index === lastIndex) {
          label += 'or more)';
        } else {
          label += `up to ${customNumericClass.max})`;
        }
      }
      return label;
    };

    return (
      <div className="text-xs text-lighter">
        <span className="text-default">Classes ({savedCustomSegmentDetails.length})</span>
        <ul style={{ listStyle: 'disc inside' }} className="pl-2.5">
          {savedCustomSegmentDetails.map((savedCustomSegmentDetail, index) => {
            return (
              <li key={index} className="text-xs">
                {getLabel(savedCustomSegmentDetail, index)}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <div className="relative bg-white border border-default rounded-2 overflow-hidden mb-2">
      <div
        className={classNames('overflow-hidden px-2 pt-4 pb-1', {
          'border-brand border-l-4': isActive,
        })}
      >
        <div className="mb-1">
          {savedCardLabels.map((label, index) => (
            // to add gap between the badges
            <span className="inline-block mr-1" key={label + index}>
              <Badge state="medium"> {label?.toUpperCase()}</Badge>
            </span>
          ))}
        </div>
        <Accordion iconRight flushAlign>
          <Accordion.Item title={savedData.name}>
            <div className="text-sm text-lighter pb-1.5">{savedData.description}</div>
            {renderAccordionDetails(savedData)}
          </Accordion.Item>
        </Accordion>
        <div className="flex-row pt-2 justify-between">
          <div>
            <Button
              label={customSegmentDetails.savedCardLabels.applyButtonLabels}
              testId={`${customSegmentDetails.savedCardLabels.testId}-apply-btn`}
              mode="simple"
              disabled={isActive || !isApplicable}
              onClick={() => handleApply(savedData)}
            >
              <Button.Icon>
                <Button.Icon.icons.Check />
              </Button.Icon>
              Apply
            </Button>
          </div>
          <div className="flex-row justify-end gap-1">
            <div>
              <Button
                label={customSegmentDetails.savedCardLabels.shareButtonLabels}
                testId={`${customSegmentDetails.savedCardLabels.testId}-share-btn`}
                mode="simple"
                onClick={() => {
                  handleShare(savedData);
                }}
                disabled={isViewer(savedData.createdBy, owner.userId)}
              >
                <Button.Icon>
                  <Button.Icon.icons.Share />
                </Button.Icon>
              </Button>
            </div>
            <div>
              <Button
                label={customSegmentDetails.savedCardLabels.deleteButtonLabels}
                mode="simple"
                onClick={toggleDeleteModal}
                testId={`${customSegmentDetails.savedCardLabels.testId}-delete-btn`}
              >
                <Button.Icon>
                  <Button.Icon.icons.Delete />
                </Button.Icon>
              </Button>
            </div>
          </div>
        </div>
        {isActive && <div className="absolute top-0 left-0 h-full w-1 bg-brand" />}
        {renderDeleteSavedFeatureSegmentsModal()}
      </div>
    </div>
  );
};
